// « Premiers pas » : parcours en quelques étapes pour une entreprise qui ouvre Ekera. L'état de chaque étape vient de
// l'API (pièces déposées, lecture, entretien, carte, rapports) ; aucun appel nouveau. Refermable, et rouvrable depuis
// l'aide. Une lecture qui échoue laisse l'étape « État non lu » sans rien casser.
import { useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { useEkera } from '../contexte';
import {
  abonnerPremiersPas,
  calculerEtapes,
  EXPORTS_UTILES,
  etapesFaites,
  fermerPremiersPas,
  LIBELLE_ETAT_ETAPE,
  premiersPasFermes,
  TON_ETAT_ETAPE,
} from '../domaine/premiersPas';
import { typo } from '../domaine/typo';
import { lienVers } from '../routeur';
import { Etiquette } from './communs';

export function usePremiersPasFermes(cid: string): boolean {
  return useSyncExternalStore(abonnerPremiersPas, () => premiersPasFermes(cid));
}

export function PremiersPas() {
  const { client, cid, ecritureAutorisee, motifLectureSeule } = useEkera();
  const ferme = usePremiersPasFermes(cid);
  // Mêmes clés que les écrans concernés : les réponses sont partagées, pas redemandées.
  const depots = useQuery({
    queryKey: ['carte', cid, 'depots'],
    queryFn: () => client.depots(cid),
    refetchInterval: (q) => (q.state.data?.lecture?.etat === 'en_cours' ? 2000 : false),
    enabled: !ferme,
  });
  const entretien = useQuery({ queryKey: ['carte', cid, 'entretien'], queryFn: () => client.entretien(cid), enabled: !ferme });
  const rapports = useQuery({ queryKey: ['carte', cid, 'rapports'], queryFn: () => client.rapports(cid), enabled: !ferme });
  const carte = useQuery({ queryKey: ['carte', cid], queryFn: () => client.carte(cid), enabled: !ferme });
  if (ferme) return null;
  const etapes = calculerEtapes({
    depots: depots.data,
    entretien: entretien.data,
    rapports: rapports.data,
    objets: carte.data ? (carte.data.objets ?? []).filter((o) => !o.retire).length : undefined,
  });
  const faites = etapesFaites(etapes);
  const sansPiece = depots.data?.pieces.length === 0;
  // Seule l'étape suivante (la première à faire ou en cours) porte le bouton principal ; les autres restent discrets.
  const suivante = etapes.findIndex((e) => e.etat === 'a_faire' || e.etat === 'en_cours');
  return (
    <section className="panneau premiers-pas" aria-labelledby="t-premiers-pas" data-testid="premiers-pas">
      <div className="panneau-tete">
        <h2 id="t-premiers-pas">Premiers pas avec Ekera</h2>
        <button
          type="button"
          className="btn petit"
          onClick={() => {
            fermerPremiersPas(cid);
            document.getElementById('contenu')?.focus();
          }}
        >
          Fermer ce parcours
        </button>
      </div>
      <p>
        {typo(`À votre rythme : ${faites} étape${faites > 1 ? 's' : ''} faite${faites > 1 ? 's' : ''} sur ${etapes.length}. Vous pourrez rouvrir ce parcours depuis l’aide.`)}
      </p>
      {!ecritureAutorisee && (
        <p className="encadre info" data-testid="premiers-pas-lecture">
          {motifLectureSeule} Les trois premières étapes demandent le droit d’écriture : demandez à une personne qui l’a de déposer les pièces.
        </p>
      )}
      <ol className="etapes">
        {etapes.map((e, i) => (
          <li key={e.id} data-testid={`etape-${e.id}`} data-etat={e.etat}>
            <span className="n" aria-hidden="true">
              {i + 1}
            </span>
            <div className="corps">
              <h3>
                {e.titre} <Etiquette ton={TON_ETAT_ETAPE[e.etat]}>{LIBELLE_ETAT_ETAPE[e.etat]}</Etiquette>
              </h3>
              <p>{e.detail}</p>
              {(e.etat !== 'fait' || e.id === 'depot') && (
                <a className={`btn petit ${i === suivante ? 'principal' : ''}`} href={lienVers(e.page)}>
                  {e.action}
                </a>
              )}
            </div>
          </li>
        ))}
      </ol>
      <details open={sansPiece} className="exports-utiles">
        <summary>Quels exports déposer pour commencer ?</summary>
        <p className="note">
          {typo('Rien n’est obligatoire : chaque export déposé enrichit la carte. Les pièces rédigées (schémas, politiques, procédures, contrats) en PDF, Word ou Excel sont lues aussi.')}
        </p>
        <ul className="liste-points">
          {EXPORTS_UTILES.map((x) => (
            <li key={x.nature} className="ok">
              <a href={lienVers('depot', { nature: x.nature })}>
                <b>{x.libelle}</b>
              </a>
              <small>{x.aide}</small>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
